import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

/**
 * Only the texts the SHELL owns are imported here.
 *
 * Every module registers its own when it loads, from its
 * `router/modules/*.routes.js`, using `registerModuleTranslations`. That is
 * what lets a module take its texts with it when it moves to its own
 * repository: nothing here has to know it existed.
 *
 * Adding a module's texts back to this file would undo that - the shell would
 * stop compiling the day that folder is gone.
 */

// Shared by everyone: buttons, errors, filters, pagination...
// These travel with @natura/core in phase 2, so a module running standalone
// still has them. See `registerSharedTranslations`.
import common from './translations/common';

// The shell's own screens and chrome.
import menu from './translations/menu';
import profile from './translations/profile';
import dashboards from './translations/dashboards';
import login from './translations/login';
import recover from './translations/recover';

// Owned by the shared UI layer. When `ui` becomes @natura/ui these travel
// with it and the package registers them itself.
import datepicker from './translations/datepicker';
import fileUploader from './translations/fileUploader';

const shell = {
    EN: {
        ...common.EN,
        menu: menu.EN,
        profile: profile.EN,
        dashboards: dashboards.EN,
        login: login.EN,
        recover: recover.EN,
        datepicker: datepicker.EN,
        fileUploader: fileUploader.EN,
        validation: {
            requiredField: 'This field is required',
            invalidField: 'The field format is invalid',
            notFound: 'No user found with the provided email.',
            invalidLogin: 'The credentials you entered are incorrect.',
            passwordMismatch: 'Password and Confirm Password didn\'t match.',
            acceptTerms: 'You must accept the terms and conditions'
        },
        changePassword: {
            title: 'Change password',
            warning: 'Are you sure you want to change password?',
            success: 'Please check your mailbox and follow the steps to continue the process'
        },
        logout: 'Logout',
    },
    ES: {
        ...common.ES,
        menu: menu.ES,
        profile: profile.ES,
        dashboards: dashboards.ES,
        login: login.ES,
        recover: recover.ES,
        datepicker: datepicker.ES,
        fileUploader: fileUploader.ES,
        validation: {
            requiredField: 'Este campo es obligatorio',
            invalidField: 'El formato del campo es inválido',
            notFound: 'No se encontró el usuario con el email ingresado.',
            invalidLogin: 'Las credenciales ingresadas son incorrectas.',
            passwordMismatch: 'Las contraseñas no coinciden.',
            acceptTerms: 'Debes aceptar los términos y condiciones'
        },
        changePassword: {
            title: 'Cambiar contraseña',
            warning: '¿Está seguro que quiere cambiar de contraseña?',
            success: 'Por favor revisar la casilla de mail ingresada y seguir los pasos para recuperar la contraseña.'
        },
        logout: 'Cerrar sesión',
    },
};

const resources = {
    en: { translation: shell.EN },
    es: { translation: shell.ES },
};

i18n
    .use(initReactI18next) // passes i18n down to react-i18next
    .use(LanguageDetector)
    .init({
        resources,
        // Fallback language if the detected language is not available or a translation key is missing
        fallbackLng: 'es',
        lng: 'es', // language to use, more information here: https://www.i18next.com/overview/configuration-options#languages-namespaces-resources
        // you can use the i18n.changeLanguage function to change the language manually: https://www.i18next.com/overview/api#changelanguage

        interpolation: {
            escapeValue: false // react already safes from xss
        },
        react: {
            bindI18n: 'languageChanged',
        },

        // A key that does not exist is not an error for the compiler: `t('x.y')`
        // is just a string passed to a function, so the build succeeds and the
        // screen quietly renders `x.y` instead of the text. This turns that
        // silent bug into a warning while developing.
        //
        // Development only: in production the handler is never installed, so
        // there is no console output and no behaviour change - a missing key
        // still renders as the key itself, exactly as before.
        saveMissing: process.env.NODE_ENV === 'development',
        missingKeyHandler: process.env.NODE_ENV === 'development'
            ? (languages, ns, key) => {
                // Several widgets in `ui` translate a value only when it looks
                // like a key: `/\..*\./.test(text) ? t(text) : text`. A formatted
                // document number ("40.999.887") matches that test, reaches
                // i18next and comes back unchanged - which is the intended
                // behaviour, not a missing translation. Warning about those
                // would bury the real ones, so only key-shaped strings are
                // reported: they start with a letter and have dot separated
                // word segments.
                if (!/^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z0-9_-]+)+$/.test(key)) return;
                console.warn(`[i18n] falta la clave "${key}" en ${languages.join(', ')}`);
            }
            : undefined,
    });

export default i18n;
